import { useState, useMemo } from "react";
import { useNavigate, useSearchParams, Link } from "react-router-dom";
import axios from "axios";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Card, CardHeader, CardTitle, CardDescription, CardContent } from "@/components/ui/card";
import Logo from "@/components/Logo";
import { Lock, Eye, EyeOff, AlertTriangle, CheckCircle2 } from "lucide-react";

import { BACKEND_URL as API } from "@/lib/api";

export default function ResetPassword() {
  const [params] = useSearchParams();
  const nav = useNavigate();
  const token = params.get("token") || "";
  const [pw, setPw] = useState("");
  const [pw2, setPw2] = useState("");
  const [show, setShow] = useState(false);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState("");
  const [done, setDone] = useState(false);

  const strength = useMemo(() => ({
    len: pw.length >= 8,
    letter: /[a-zA-Z]/.test(pw),
    digit: /\d/.test(pw),
    match: pw.length > 0 && pw === pw2,
  }), [pw, pw2]);
  const allGood = strength.len && strength.letter && strength.digit && strength.match;

  const submit = async (e) => {
    e.preventDefault();
    setError("");
    if (!allGood) return;
    setBusy(true);
    try {
      await axios.post(`${API}/api/auth/reset-password`, { token, new_password: pw });
      setDone(true);
      setTimeout(() => nav("/login", { replace: true }), 2000);
    } catch (err) {
      setError(err?.response?.data?.detail || "Couldn't reset your password. Request a new link.");
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="min-h-screen flex items-center justify-center px-5 py-12" style={{ backgroundColor: "var(--ink-1)" }}>
      <div className="w-full max-w-md">
        <div className="flex justify-center mb-6"><Logo size="md" variant="lockup" dark linkTo="/" /></div>
        <Card data-testid="reset-password-page">
          <CardHeader>
            <CardTitle>{done ? "Password updated" : "Choose a new password"}</CardTitle>
            <CardDescription>
              {done
                ? "All set — taking you to sign in…"
                : "Type your new password twice. It must be at least 8 characters with at least one letter and one number."}
            </CardDescription>
          </CardHeader>
          <CardContent>
            {!token ? (
              <div className="flex items-start gap-3 rounded-lg border border-rose-500/30 bg-rose-500/10 p-3 text-sm text-rose-200">
                <AlertTriangle className="h-5 w-5 shrink-0 mt-0.5" />
                <div>
                  <div className="font-semibold text-white">Missing reset token</div>
                  <div className="text-xs mt-0.5">This URL is incomplete. Request a fresh link from the forgot-password page.</div>
                  <Link to="/forgot-password" className="text-xs underline mt-2 inline-block">Request new link →</Link>
                </div>
              </div>
            ) : done ? (
              <div className="flex items-center gap-3 rounded-lg border border-emerald-500/30 bg-emerald-500/10 p-3 text-sm text-emerald-200">
                <CheckCircle2 className="h-5 w-5 shrink-0" />
                <span>Your password has been changed. Redirecting to sign-in…</span>
              </div>
            ) : (
              <form onSubmit={submit} className="space-y-4">
                <div>
                  <Label htmlFor="pw">New password</Label>
                  <div className="relative mt-1.5">
                    <Lock className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-white/40" />
                    <Input
                      id="pw" type={show ? "text" : "password"} required autoFocus
                      value={pw} onChange={(e) => setPw(e.target.value)}
                      className="pl-9 pr-10" data-testid="reset-pw-input"
                    />
                    <button type="button" onClick={() => setShow((s) => !s)}
                            className="absolute right-2 top-1/2 -translate-y-1/2 p-1.5 text-white/40 hover:text-white">
                      {show ? <EyeOff className="h-4 w-4" /> : <Eye className="h-4 w-4" />}
                    </button>
                  </div>
                </div>
                <div>
                  <Label htmlFor="pw2">Confirm new password</Label>
                  <Input
                    id="pw2" type={show ? "text" : "password"} required
                    value={pw2} onChange={(e) => setPw2(e.target.value)}
                    className="mt-1.5" data-testid="reset-pw2-input"
                  />
                </div>

                <ul className="text-xs space-y-0.5">
                  <Rule ok={strength.len}    text="At least 8 characters" />
                  <Rule ok={strength.letter} text="Contains a letter" />
                  <Rule ok={strength.digit}  text="Contains a digit" />
                  <Rule ok={strength.match}  text="Both passwords match" />
                </ul>

                {error && (
                  <div className="text-sm text-rose-300 rounded border border-rose-500/30 bg-rose-500/10 p-2">{error}</div>
                )}
                <Button type="submit" className="w-full" disabled={busy || !allGood} data-testid="reset-submit">
                  {busy ? "Updating…" : "Set new password"}
                </Button>
              </form>
            )}
          </CardContent>
        </Card>
      </div>
    </div>
  );
}

function Rule({ ok, text }) {
  return (
    <li className={ok ? "text-emerald-300" : "text-white/40"}>
      {ok ? "✓" : "•"} {text}
    </li>
  );
}
